import React, { useEffect, useState } from "react";
import {
  ShoppingBag,
  CreditCard,
  Wallet,
  Clock,
  ArrowUpRight,
  User,
  ChevronRight,
  Phone,
  Search,
} from "lucide-react";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { getCustomerDashboardDetails } from "../../services/customerServices";
import { useTranslation } from "react-i18next";
import { formatCurrencyINR } from "../../utils/function";
import { useSelector } from "react-redux";
import CONFIG from "../../config";
import { getStoreDashboardDetails } from "../../services/storeOwnerOrderServices";
import { useNavigate } from "react-router-dom";
import { notify } from "../../components/Common/Alerts";

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let auth = useSelector((x) => x.auth.value);
  const [dashboardDetails, setDashboardDetails] = useState("");
  const [storeDashboardDetails, setStoreDashboardDetails] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (Number(auth?.user?.role_id) === Number(CONFIG.ROLE_OWNER)) {
      setLoading(true);
      let data = {
        store_id: auth.active_store_id
      }
      getStoreDashboardDetails(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);
          setStoreDashboardDetails(decrypted.data);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [auth?.user?.role_id, auth.active_store_id]);

  useEffect(() => {
    if (Number(auth?.user?.role_id) === Number(CONFIG.ROLE_CUSTOMER)) {
      getCustomerDashboardDetails()
        .then((response) => {
          const decrypted = decryptData(response.data.data);
          setDashboardDetails(decrypted.data);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [auth?.user?.role_id]);

  const filteredStoreDetails = storeDashboardDetails?.customers_pending?.filter(
    (order) => {
      const term = searchTerm?.toLowerCase() || "";

      const mobile = String(order?.mobile || "").toLowerCase();
      const name = order?.name?.toLowerCase() || "";

      return mobile.includes(term) || name.includes(term);
    },
  );
  return (
    <>
      {Number(auth?.user?.role_id) === Number(CONFIG.ROLE_CUSTOMER) ? (
        <div className="h-screen overflow-y-auto bg-[#FDFCFB] dark:bg-[#050C1A] font-sans no-scrollbar scroll-smooth">
          <div className="max-w-[1440px] mx-auto p-4 md:p-8 lg:p-12 pb-32">
            <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-xl md:text-[20px] lg:text-[20px] font-black text-slate-800 dark:text-white uppercase tracking-tighter leading-none">
                  {t("account") || "Account"}{" "}
                  <span className="text-orange-500">{t("overview")}</span>
                </h1>
                <p className="text-[11px] md:text-xs font-bold text-slate-400 dark:text-slate-500 mt-2 uppercase tracking-widest">
                  {t("welcomeText")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-10">
              <div className="grid grid-cols-2 gap-3 lg:contents">
                <div
                  className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-4 lg:p-8 rounded-[1.75rem] lg:rounded-[2.5rem] shadow-sm hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate("/order")}
                >
                  <div className="w-8 h-8 lg:w-12 lg:h-12 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg lg:rounded-2xl flex items-center justify-center mb-3">
                    <ShoppingBag
                      className="text-emerald-500 w-[18px] h-[18px] md:w-[24px] md:h-[24px]"
                      strokeWidth={2.5}
                    />
                  </div>
                  <p className="text-[9px] lg:text-[10px] text-zinc-400 dark:text-slate-500 uppercase font-black tracking-wider">
                    {t("totalOrders")}
                  </p>
                  <p className="text-lg lg:text-2xl font-black text-zinc-900 dark:text-white mt-0.5">
                    {dashboardDetails?.total_orders ?? 0}
                  </p>
                </div>

                <div className="bg-orange-500 dark:bg-orange-600 p-4 lg:p-8 rounded-[1.75rem] lg:rounded-[2.5rem] shadow-lg shadow-orange-200 dark:shadow-none">
                  <div className="w-8 h-8 lg:w-12 lg:h-12 bg-white/20 rounded-lg lg:rounded-2xl flex items-center justify-center mb-3">
                    <CreditCard
                      className="text-white w-[18px] h-[18px] md:w-[24px] md:h-[24px]"
                      strokeWidth={2.5}
                    />
                  </div>
                  <p className="text-[9px] lg:text-[10px] text-orange-100 uppercase font-black tracking-wider opacity-90">
                    {t("totalPurchase")}
                  </p>
                  <p className="text-lg lg:text-2xl font-black text-white mt-0.5">
                    {formatCurrencyINR(dashboardDetails?.total_purchase ?? 0)}
                  </p>
                </div>
              </div>

              <div className="bg-orange-600 p-5 md:p-7 lg:p-10 rounded-[2rem] lg:rounded-[2.5rem] shadow-lg shadow-orange-500/20 text-white flex flex-col justify-center">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[10px] lg:text-xs font-black text-orange-100 uppercase tracking-widest">
                      {t("pendingAmount")}
                    </p>
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-black mt-2 tracking-tighter">
                      {formatCurrencyINR(
                        dashboardDetails?.pending_payments?.total_amount ?? 0,
                      )}
                    </h2>
                  </div>
                  <div className="bg-white/20 p-3 lg:p-5 rounded-xl lg:rounded-2xl backdrop-blur-sm">
                    <Wallet className="w-5 h-5 lg:w-7 lg:h-7" />
                  </div>
                </div>
              </div>
            </div>

            {dashboardDetails?.pending_payments?.orders?.length > 0 && (
              <div className="bg-white dark:bg-[#0A1221] rounded-[2.5rem] lg:rounded-[3.5rem] border border-slate-100 dark:border-slate-800/60 shadow-sm overflow-hidden">
                <div className="p-6 md:p-8 lg:p-10 border-b border-slate-50 dark:border-slate-800/50">
                  <h3 className="text-sm md:text-[15px] lg:text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">
                    {t("pendingByStore")}
                  </h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 divide-slate-50 dark:divide-slate-800/40">
                  {dashboardDetails?.pending_payments?.orders?.map(
                    (store, index) => (
                      <div
                        key={index}
                        className={`p-5 md:p-6 lg:p-10 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group cursor-pointer lg:border-r last:border-r-0 border-slate-50 dark:border-slate-800/40`}
                      >
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl overflow-hidden border border-slate-200/50 dark:border-slate-700 shrink-0">
                          {store?.store_image ? (
                            <img
                              src={store.store_image}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-slate-400">
                              NA
                            </div>
                          )}
                        </div>
                        <div className="truncate">
                          <h4 className="font-black text-slate-700 dark:text-slate-200 text-sm md:text-base lg:text-lg leading-tight uppercase truncate">
                            {store.store_name}
                          </h4>
                          <p className="text-lg md:text-xl lg:text-2xl font-black text-red-500 tracking-tighter">
                            {formatCurrencyINR(store.pending_total)}
                          </p>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {Number(auth?.user?.role_id) === Number(CONFIG.ROLE_OWNER) && (
            <div className="min-h-screen bg-[#FDFCFB] dark:bg-slate-950 text-zinc-800 dark:text-slate-200 p-4 pb-24 font-sans transition-colors duration-300">
              {/* Restrict width on massive monitors for readability */}
              <div className="max-w-[1440px] mx-auto">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-5 lg:mb-10 mt-2">
                  <div className="lg:py-4">
                    <h1 className="text-xl lg:text-2xl font-black text-zinc-900 dark:text-white tracking-tighter">
                      {t("dukaanSetu")}
                    </h1>
                    <p className="text-orange-600 dark:text-orange-500 text-[9px] lg:text-xs uppercase tracking-[0.15em] font-black opacity-80">
                      {t("storeManagement")}
                    </p>
                  </div>

                  {loading ? (
                    <div className="h-10 w-28 bg-orange-100 dark:bg-slate-900 rounded-xl animate-pulse" />
                  ) : (
                    <div
                      className="flex items-center gap-2 bg-[#1A2B47] dark:bg-orange-500 text-white px-4 lg:px-8 py-2.5 lg:py-4 rounded-xl lg:rounded-2xl shadow-md active:scale-95 transition-transform cursor-pointer"
                      onClick={() => navigate("/order-store-owner")}
                    >
                      <span className="text-xs lg:text-base font-bold">
                        {t("newOrder")}
                      </span>
                      <ShoppingBag size={18} strokeWidth={2.5} />
                    </div>
                  )}
                </div>

                <div className="lg:flex lg:gap-8 lg:items-start">
                  <div className="lg:w-[40%] space-y-5 lg:sticky lg:top-8">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-4 lg:p-8 rounded-[1.75rem] shadow-sm">
                        <div className="w-8 h-8 lg:w-12 lg:h-12 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg lg:rounded-xl flex items-center justify-center mb-3 lg:mb-5">
                          <Wallet
                            className="text-emerald-500"
                            size={20}
                            strokeWidth={2.5}
                          />
                        </div>
                        <p className="text-[9px] lg:text-[11px] text-zinc-400 dark:text-slate-500 uppercase font-black tracking-wider">
                          {t("totalRevenue")}
                        </p>
                        <p className="text-lg lg:text-2xl font-black text-zinc-900 dark:text-white mt-0.5">
                          {formatCurrencyINR(
                            storeDashboardDetails?.dashboard?.total_revenue ||
                              0,
                          )}
                        </p>
                      </div>

                      <div className="bg-orange-500 dark:bg-orange-600 p-4 lg:p-8 rounded-[1.75rem] shadow-lg shadow-orange-200 dark:shadow-none">
                        <div className="w-8 h-8 lg:w-12 lg:h-12 bg-white/20 rounded-lg lg:rounded-xl flex items-center justify-center mb-3 lg:mb-5">
                          <Clock
                            className="text-white"
                            size={20}
                            strokeWidth={2.5}
                          />
                        </div>
                        <p className="text-[9px] lg:text-[11px] text-orange-100 uppercase font-black tracking-wider opacity-90">
                          {t("pendingPayments")}
                        </p>
                        <p className="text-lg lg:text-2xl font-black text-white mt-0.5">
                          {formatCurrencyINR(
                            storeDashboardDetails?.dashboard
                              ?.total_pending_amount || 0,
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-slate-900 border border-slate-50 dark:border-slate-800 rounded-[1.5rem] lg:rounded-[2.5rem] p-4 lg:p-10 shadow-sm flex items-center justify-around relative overflow-hidden lg:h-40">
                      <div className="absolute top-0 left-0 w-1 lg:w-2 h-full bg-orange-500/20" />
                      <div
                        className="text-center py-1 cursor-pointer"
                        onClick={() =>
                          navigate("/all-store-owner-order-list", { state: 1 })
                        }
                      >
                        <p className="text-[9px] lg:text-xs text-zinc-400 dark:text-slate-500 uppercase font-black tracking-tighter mb-0.5">
                          {t("todayOrder")}
                        </p>
                        <p className="text-xl lg:text-4xl font-black text-zinc-900 dark:text-white">
                          {storeDashboardDetails?.dashboard?.today_total_orders}
                        </p>
                      </div>
                      <div className="h-8 lg:h-16 w-[1px] bg-slate-100 dark:bg-slate-800"></div>
                      <div className="text-center py-1">
                        <p className="text-[9px] lg:text-xs text-zinc-400 dark:text-slate-500 uppercase font-black tracking-tighter mb-0.5">
                          {t("todayRevenue")}
                        </p>
                        <p className="text-xl lg:text-4xl font-black text-orange-600 dark:text-orange-500">
                          {formatCurrencyINR(
                            storeDashboardDetails?.dashboard?.today_revenue ||
                              0,
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="lg:w-[60%] mt-6 lg:mt-0 lg:bg-white lg:dark:bg-slate-900 lg:p-8 lg:rounded-[2.5rem] lg:border lg:border-slate-100 lg:dark:border-slate-800 lg:shadow-sm">
                    {storeDashboardDetails?.customers_pending?.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center px-1">
                          <div>
                            <h2 className="text-lg lg:text-2xl font-extrabold text-zinc-900 dark:text-white tracking-tight">
                              {t("pendingPayments")}
                            </h2>
                            <div className="h-1 lg:h-1.5 w-8 lg:w-12 bg-orange-500 rounded-full mt-1" />
                          </div>
                          <span className="text-[10px] lg:text-xs font-black text-orange-600 dark:text-orange-400 uppercase tracking-tighter bg-orange-100 dark:bg-orange-950/40 px-3 lg:px-5 py-1.5 rounded-full border border-orange-200">
                            {storeDashboardDetails.customers_pending.length}{" "}
                            {t("customer")}
                          </span>
                        </div>

                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 lg:h-5 lg:w-5 text-orange-500" />
                          </div>
                          <input
                            type="text"
                            placeholder={t("searchStoreDetails")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-white dark:bg-slate-950 border border-orange-200 dark:border-slate-800 rounded-xl lg:rounded-2xl pl-12 pr-4 py-3 lg:py-4 text-sm lg:text-base outline-none focus:ring-2 focus:ring-[#E67E22] focus:border-[#E67E22] dark:focus:ring-orange-500 dark:focus:border-orange-500 transition-all shadow-sm"
                          />
                        </div>
                      </div>
                    )}

                    <div className="bg-[#FDFCFB] dark:bg-slate-950 pb-32">
                      {/* Header and Stats would go here */}

                      {/* Main Customer List Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4 mt-6">
                        {loading ? (
                          Array(4)
                            .fill(0)
                            .map((_, index) => (
                              <div
                                key={index}
                                className="h-24 bg-slate-50 dark:bg-slate-800 animate-pulse rounded-2xl"
                              />
                            ))
                        ) : filteredStoreDetails?.length > 0 ? (
                          filteredStoreDetails.map((customer) => (
                            <div
                              key={customer.id}
                              className={`p-4 rounded-2xl flex items-center justify-between transition-all border cursor-pointer active:scale-[0.98] ${
                                selectedId === customer.id
                                  ? "border-orange-500 bg-orange-50 dark:bg-orange-900/30 shadow-md"
                                  : "bg-white dark:bg-slate-900 border-slate-50 dark:border-slate-800 shadow-sm"
                              }`}
                              onClick={() => {
                                setSelectedId(customer.id);
                                navigate(`/pending-order/${customer.id}`, {
                                  state: { item: customer },
                                });
                              }}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 lg:w-14 lg:h-14 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center border border-slate-100 dark:border-slate-700">
                                  <User size={24} className="text-orange-500" />
                                </div>
                                <div className="min-w-0">
                                  <h3 className="font-bold text-sm lg:text-base text-zinc-800 dark:text-slate-200 truncate max-w-[140px] lg:max-w-none">
                                    {customer.name}
                                  </h3>
                                  <p className="text-[10px] lg:text-xs text-zinc-400 flex items-center gap-1 font-bold">
                                    <Phone size={10} strokeWidth={3} />{" "}
                                    {customer.mobile}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-base lg:text-xl font-black text-orange-600 dark:text-orange-500 tracking-tighter">
                                  {formatCurrencyINR(
                                    customer.total_pending || 0,
                                  )}
                                </p>
                                <span className="text-[9px] bg-red-50 dark:bg-red-900/20 text-red-500 px-2 py-0.5 rounded font-black uppercase tracking-tighter">
                                  {customer.pending_orders} {t("dues")}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="col-span-full text-center py-20 text-slate-400 font-bold">
                            {t("noDataFound")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Dashboard;
